import { useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { fmtDate, gbp, usePlan, useRefreshPlan } from "@/lib/plan";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function statusLine(p) {
  const s = p.subscription;
  if (p.source === "early_supporter") return `Free early-supporter Pro · ${p.days_left} day${p.days_left === 1 ? "" : "s"} left (ends ${fmtDate(p.early_supporter_until)})`;
  if (p.source === "admin") return "Pro granted by ROLLSMARTR";
  if (p.source === "trial") return s.cancel_at_period_end ? `Trial ends ${fmtDate(s.trial_end)} · won't renew` : `Free trial until ${fmtDate(s.trial_end)} · then ${gbp(s.price)}/month`;
  if (p.source === "subscription") {
    if (s.status === "past_due") return "Payment failed · update your card to keep Pro";
    return s.cancel_at_period_end ? `Cancelled · Pro until ${fmtDate(s.current_period_end)}` : `${gbp(s.price)}/month · renews ${fmtDate(s.current_period_end)}`;
  }
  return "Free plan";
}

export function PlanCard() {
  const { data: p } = usePlan();
  const refresh = useRefreshPlan();
  const [busy, setBusy] = useState(null);
  if (!p) return <div className="card h-28 animate-pulse" />;
  const paid = ["subscription", "trial"].includes(p.source);
  const act = (key, fn) => async () => {
    setBusy(key);
    try {
      await fn();
      await refresh();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };
  const portal = act("portal", async () => {
    const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
    window.location.href = data.url;
  });
  const cancel = act("cancel", async () => {
    await api.post("/billing/cancel");
    toast.success("Subscription cancelled", { description: "You keep Pro until the end of the current period. You won't be charged again." });
  });
  const resume = act("resume", () => api.post("/billing/resume").then(() => toast.success("Subscription resumed")));
  const u = p.usage;
  return (
    <section className="card p-5" data-testid="profile-plan">
      <div className="flex items-center justify-between gap-3">
        <div className="eyebrow">Plan</div>
        <span className={`text-[11px] font-bold uppercase tracking-wider rounded px-2 py-0.5 flex items-center gap-1 ${p.plan === "pro" ? "bg-[#E50914] text-white" : "bg-white/10 text-[#A1A1AA]"}`} data-testid="profile-plan-badge">
          {p.plan === "pro" && <Crown size={11} />}{p.plan === "pro" ? "Pro" : "Free"}
        </span>
      </div>
      <div className="text-sm mt-2" data-testid="profile-plan-status">{statusLine(p)}</div>
      {p.plan === "free" && (
        <div className="text-xs text-[#A1A1AA] mt-2 space-y-0.5" data-testid="profile-plan-usage">
          <div>AI journal entries: {u.ai_logs_used} of {u.ai_logs_limit} used (rolling 7 days)</div>
          <div>Quiz questions today: {u.quiz_questions_used} of {u.quiz_questions_limit}</div>
        </div>
      )}
      <div className="mt-4 space-y-2">
        {!paid && <Link to="/pro" className="btn-primary w-full" data-testid="profile-upgrade-button">{p.source === "early_supporter" ? "Keep Pro" : p.trial_eligible ? "Try Pro free for 7 days" : "Upgrade to Pro"}</Link>}
        {paid && p.subscription.cancel_at_period_end && (
          <button onClick={resume} disabled={!!busy} className="btn-primary w-full" data-testid="profile-resume-button">{busy === "resume" ? <Loader2 size={16} className="animate-spin" /> : "Resume subscription"}</button>
        )}
        {paid && !p.subscription.cancel_at_period_end && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button className="btn-secondary w-full" disabled={!!busy} data-testid="profile-cancel-button">{busy === "cancel" ? <Loader2 size={16} className="animate-spin" /> : "Cancel subscription"}</button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-[#111113] border-[#27272A]">
              <AlertDialogHeader>
                <AlertDialogTitle>Cancel Pro?</AlertDialogTitle>
                <AlertDialogDescription>
                  {p.source === "trial" ? `Your trial ends ${fmtDate(p.subscription.trial_end)} and you won't be charged.` : `You'll keep Pro until ${fmtDate(p.subscription.current_period_end)} and won't be charged again.`} Your journal and quiz history stay. {p.subscription.tier === "founder" && "Note: the £4.99 founder price can't be restored once your subscription ends."}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="profile-cancel-keep">Keep Pro</AlertDialogCancel>
                <AlertDialogAction onClick={cancel} className="bg-[#E50914] hover:bg-[#B80710]" data-testid="profile-cancel-confirm">Cancel subscription</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
        {p.can_manage && (
          <button onClick={portal} disabled={!!busy} className="w-full h-10 text-sm text-[#A1A1AA] hover:text-white" data-testid="profile-billing-portal">
            {busy === "portal" ? <Loader2 size={15} className="animate-spin mx-auto" /> : "Billing, invoices & payment method"}
          </button>
        )}
      </div>
    </section>
  );
}
